import { FC, useCallback, useState } from "react";
import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import { closeDrawer } from "actions/drawerActions";
import { useDispatch } from "react-redux";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { useCustomDrawerStyles } from "../styles";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { SCHEDULE_TABLE_COLUMNS, SCHEDULE_ROLE_SETTINGS } from "pages/Schedule/scheduleTable";
import { scheduleSetFilter } from "actions/scheduleActions";
import { MultipleSelectField } from "componentsNew/elements/MultipleSelectField";
import { ALL_USER_ROLES, daStatusConstants } from "constants/constants";

interface ScheduleTableSettingsProps {}

export const ScheduleTableSettings: FC<ScheduleTableSettingsProps> = () => {
  const classes = useCustomDrawerStyles();
  const dispatch = useDispatch();
  const { scheduleFilters } = useTypeSelectors((state) => state.scheduleTable);
  const [isSecondWeek, setIsSecondWeek] = useState(scheduleFilters.isTwoWeekTable);
  const [activeCols, setActiveCols] = useState<string[]>(scheduleFilters?.activeColumns || []);
  const [statusList, setStatusList] = useState<number[]>(
    scheduleFilters?.statusList?.length ? scheduleFilters?.statusList : [1],
  );
  const [userRoles, setUserRoles] = useState<number[]>(scheduleFilters?.userRoles || []);
  const [scheduleRoles, setScheduleRoles] = useState<number[]>(scheduleFilters?.shiftRoles || []);

  const handleEditCols = (id: string) => {
    setActiveCols((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const closenModal = () => dispatch(closeDrawer());

  const handleFilterSubmit = () => {
    closenModal();
    dispatch(
      scheduleSetFilter({
        isTwoWeekTable: isSecondWeek,
        activeColumns: activeCols,
        statusList,
        userRoles,
        shiftRoles: scheduleRoles,
      }),
    );
  };

  const newDataStatusList = daStatusConstants.filter((status) => [1, 5].includes(status.value))

  const handleChangeStatuses = useCallback((statuses: number[]) => {
    setStatusList(statuses);
  }, []);

  const handleSelectAllStatuses = useCallback(() => {
    setStatusList((prev) =>
      prev.length !== newDataStatusList.length
        ? newDataStatusList.map((status) => status.value)
        : [],
    );
  }, [newDataStatusList]);

  const handleChangeUserRole = useCallback((userRoles: number[]) => {
    setUserRoles(userRoles);
  }, []);

  const handleSelectAllUserRole = useCallback(() => {
    setUserRoles((prev) =>
      prev.length !== ALL_USER_ROLES.length ? ALL_USER_ROLES.map((status) => status.value) : [],
    );
  }, []);

  const handleChangeScheduleRoles = useCallback((roles: number[]) => {
    setScheduleRoles(roles);
  }, []);

  return (
    <>
      <DialogContent className={classes.drawerContent}>
        <Box className={classes.drawerSection}>
          <Box className={classes.horizontalBox}>
            <Typography style={{ fontWeight: 600 }}>Enable 2-Week Table</Typography>
            <Switch checked={isSecondWeek} onChange={() => setIsSecondWeek(!isSecondWeek)} />
          </Box>
        </Box>
        <Box className={classes.drawerSection}>
          <Typography style={{ fontWeight: 600 }}>Show Table Columns</Typography>
          <Box className={classes.verticalBox} style={{ marginTop: 16 }}>
            {SCHEDULE_TABLE_COLUMNS.filter((col) => col.id !== "checkbox" && !col.dateCol).map((item) => (
              <FormControlLabel
                key={item?.id}
                onChange={() => handleEditCols(item?.id)}
                checked={activeCols.includes(item?.id)}
                control={<Checkbox />}
                label={<Typography style={{ marginRight: "10px" }}>{item?.label}</Typography>}
              />
            ))}
          </Box>
        </Box>
        <Box className={classes.drawerSection}>
          <Typography style={{ fontWeight: 600 }}>Filter Table</Typography>
          <Box
            className={classes.verticalBox}
            style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}
          >
            <MultipleSelectField
              fullWidth
              options={newDataStatusList}
              selected={statusList}
              action={handleChangeStatuses}
              actionAll={handleSelectAllStatuses}
              label="User Status"
              allLabel="All Status"
            />
            <MultipleSelectField
              fullWidth
              options={ALL_USER_ROLES}
              selected={userRoles}
              action={handleChangeUserRole}
              actionAll={handleSelectAllUserRole}
              label="User Roles"
              allLabel="All User Roles"
            />
            <MultipleSelectField
              fullWidth
              hasSublists
              options={SCHEDULE_ROLE_SETTINGS.map((group) => ({
                value: group.name,
                label: group.id,
                subList: group.subList.map((item) => ({
                  value: item.id,
                  label: item.name,
                })),
              }))}
              selected={scheduleRoles}
              action={handleChangeScheduleRoles}
              label="Schedule Roles"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionSection}>
        <Box className={classes.actionButtons}>
          <Box className={classes.actionButtons}>
            <Box className={classes.actionButtonsLeft}>
              <CustomButton buttonText={"Apply"} onClick={handleFilterSubmit} variant="contained" />
              <CustomButton buttonText={"Cancel"} onClick={closenModal} variant="text" />
            </Box>
          </Box>
        </Box>
      </DialogActions>
    </>
  );
};
